import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { InvoiceCard, SearchView } from "../../../components";
import { getInvoices, downloadInvoicesExcel } from "../../../data";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  approveInvoice,
  downloadFilteredInvoicesAllExcel,
  downloadFilteredInvoicesExcel,
  getAllInvoices,
  uploadInvoiceStatusExcel,
} from "@/data/requests/invoice-requests";
import { PaymentDateModal } from "@/components/payment-date-modal";
import { useToast } from "@/components/ui/use-toast";
import AuthStore from "../../../stores/AuthStore";

const STATUS_OPTIONS = [
  { value: "Approved", label: "Approved" },
  { value: "Change Requested", label: "Change Requested" },
  { value: "Troubled", label: "Troubled" },
  { value: "Invoice Uploaded", label: "Invoice Uploaded" },
  { value: "Mail Sended", label: "Mail Sended" },
  { value: "Accounting", label: "Accounting" },
  { value: "Auto Created Invoice", label: "Auto Created Invoice" },
  { value: "OCR Completed", label: "OCR Completed" },
  { value: "LUCAREADY", label: "LUCA READY" },
  { value: "LUCA", label: "LUCA" },
  { value: "WAITING", label: "WAITING" },
  { value: "InBank", label: "InBank" },
  { value: "PAID-TIMEOUT", label: "PAID-TIMEOUT" },
  { value: "Paid", label: "Paid" },
  { value: "Not Paid", label: "Not Paid" },
  { value: "Return", label: "Return" },
  { value: "all", label: "All" },
];

export function InvoiceListAllScreen() {
  const { isAdvancedTable } = useTableView();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isPaymentDateModalOpen, setIsPaymentDateModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [pageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    name: "",
    email: "",
    lucaId: "",
    status: "",
  });

  const navigate = useNavigate();

  const [page, setPage] = useState(() => {
    const storedPage = localStorage.getItem(`invoiceListPage-all`);
    return storedPage ? parseInt(storedPage, 10) : 1;
  });
  const [queryParams, setQueryParams] = useState(() => {
    const storedQueryParams = localStorage.getItem(
      `invoiceListQueryParams-all`
    );
    if (storedQueryParams && storedQueryParams !== "{}") {
      setSearchParams(JSON.parse(storedQueryParams));
    }
    return storedQueryParams ? JSON.parse(storedQueryParams) : {};
  });
  useEffect(() => {
    localStorage.setItem(`invoiceListPage-all`, page.toString());
    localStorage.setItem(
      `invoiceListQueryParams-all`,
      JSON.stringify(queryParams)
    );
  }, [page, queryParams]);

  const { data, isLoading } = useQuery({
    queryKey: ["invoices", page, pageSize, queryParams, sortColumn, sortDirection],
    queryFn: () =>
      getAllInvoices({
        page,
        pageSize,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        lucaIdFilter: queryParams.lucaId,
        statusFilter: queryParams.status,
        sortColumn,
        sortDirection,
      }),
  });

  const downloadExcel = () => {
    downloadFilteredInvoicesAllExcel({
      nameFilter: queryParams.name,
      emailNameFilter: queryParams.email,
      lucaIdFilter: queryParams.lucaId,
      statusFilter: queryParams.status,
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "filtered_invoices.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  const uploadExcel = (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      // Implement the API call to upload the Excel file
      // This should only update the status of existing invoices
      // You'll need to create a new API endpoint for this
      uploadInvoiceStatusExcel(formData).then(() => {
        queryClient.invalidateQueries(["invoices-all"]);
      });
    }
  };

  const approveInvoiceMutation = useMutation({
    mutationFn: approveInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries(["invoices-all"]);
    },
  });

  const handleUpdateStatus = async (id, status) => {
    if (status === "Paid") {
      setSelectedInvoice(id);
      setIsPaymentDateModalOpen(true);
      return;
    }

    try {
      await approveInvoiceMutation.mutateAsync({
        id,
        data: { status },
      });
      queryClient.invalidateQueries(["invoices", id]);
      toast({
        title: "Success",
        description: "Invoice status updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update invoice status",
        variant: "destructive",
      });
    }
  };

  const handlePaymentDateConfirm = async (paymentDate) => {
    try {
      await approveInvoiceMutation.mutateAsync({
        id: selectedInvoice,
        data: {
          status: "Paid",
          paymentDate: paymentDate,
        },
      });
      queryClient.invalidateQueries(["invoices", selectedInvoice]);
      toast({
        title: "Success",
        description: "Invoice marked as paid successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update invoice status",
        variant: "destructive",
      });
    } finally {
      setIsPaymentDateModalOpen(false);
      setSelectedInvoice(null);
    }
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  const pageSettings = {
    title: "Invoice List",
    actions: [
      {
        name: "ONLINE INVOICE KONTROL",
        color:
          "bg-yellow-100 hover:bg-yellow-400 hover:text-black text-black-800",
        icon: "add-icon",
        onClick: () => navigate("/invoices-all/accounting"),
      },
      {
        name: "Download Excel",
        color: "bg-green-100 hover:bg-red-400 hover:text-black text-black-800",
        icon: "add-icon",
        onClick: downloadExcel,
      },
      {
        name: "Upload Status Excel",
        color: "bg-blue-100 hover:bg-blue-400 hover:text-black text-black-800",
        icon: "upload-icon",
        onClick: () => document.getElementById("uploadExcel").click(),
      },
      {
        name: "Back",
        color: "bg-red-100 hover:bg-red-400 hover:text-black text-red-800",
        icon: "add-icon",
        onClick: () => {
          navigate("/home");
        },
      },
    ],
  };

  // Eski tablo için basit kolonlar
  const simpleColumns = [
    {
      key: "name",
      label: "Name",
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "email",
      label: "Email",
      accessor: (item) => item.commission?.email || "",
    },
    {
      key: "commissionInvoiceId",
      label: "Our Invoice Date",
      accessor: (item) => item.commission?.invoiceId || "",
    },
    {
      key: "invoiceId",
      label: "Invoice ID",
      accessor: (item) => item.invoiceId || "",
    },
    {
      key: "bankAccountHolder",
      label: "Bank Account Holder",
      accessor: (item) => item.bankAccountHolder || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Account Name",
      accessor: (item) => item.commission?.client?.lucaAccountName || "",
    },
    {
      key: "createdAt",
      label: "Created At",
      accessor: (item) =>
        item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("tr-TR")
          : "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      accessor: (item) => item.commission?.client?.lucaId || "",
    },
    {
      key: "finalAmount",
      label: "Final Amount",
      accessor: (item) => item.commission?.finalAmount || "",
    },
    { key: "status", label: "Status" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            onClick={() =>
              navigate(
                `/payments/${row.commission.paymentId}/invoices/${row.id}`
              )
            }
          >
            View Details
          </Button>
          {row.status !== "Paid" && AuthStore.isHavePermission("MARK_PAID") && (
            <Button
              variant="destructive"
              onClick={() => handleUpdateStatus(row.id, "Paid")}
              disabled={row.status === "Paid"}
            >
              Mark as Paid
            </Button>
          )}
          {row.status !== "Luca" && row.status !== "Paid" && AuthStore.isHavePermission("MARK_LUCA") && (
            <Button
              variant="secondary"
              onClick={() => handleUpdateStatus(row.id, "LUCA")}
              disabled={row.status === "LUCA"}
            >
              LUCA
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Gelişmiş tablo için tüm kolonlar (nested data dahil)
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    {
      key: "clientName",
      label: "Client Name",
      sortable: false,
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "influencerName",
      label: "Influencer",
      sortable: false,
      accessor: (item) => item.commission?.influencerName || "",
    },
    {
      key: "commissionEmail",
      label: "Commission Email",
      sortable: false,
      accessor: (item) => item.commission?.email || "",
    },
    { key: "email", label: "Invoice Email", sortable: true },
    { key: "phoneNumber", label: "Phone", sortable: true },
    { key: "recipientName", label: "Recipient Name", sortable: true },
    { key: "bankAccountHolder", label: "Bank Account Holder", sortable: true },
    { key: "iban", label: "IBAN", sortable: false },
    { key: "bic", label: "BIC", sortable: false },
    { key: "bankName", label: "Bank Name", sortable: true },
    { key: "totalAmount", label: "Total Amount", sortable: true },
    { key: "currency", label: "Currency", sortable: true },
    { key: "invoiceId", label: "Invoice ID", sortable: true },
    { key: "invoiceDate", label: "Invoice Date", sortable: true },
    { key: "ourCompany", label: "Our Company", sortable: true },
    { key: "status", label: "Status", sortable: true },
    {
      key: "commissionInvoiceId",
      label: "Our Invoice Date",
      sortable: false,
      accessor: (item) => item.commission?.invoiceId || "",
    },
    {
      key: "finalAmount",
      label: "Final Amount",
      sortable: false,
      accessor: (item) => item.commission?.finalAmount || "",
    },
    {
      key: "managerName",
      label: "Manager",
      sortable: false,
      accessor: (item) => item.commission?.managerName || "",
    },
    {
      key: "country",
      label: "Country",
      sortable: false,
      accessor: (item) => item.commission?.country || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      sortable: false,
      accessor: (item) => item.commission?.client?.lucaId || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Account Name",
      sortable: false,
      accessor: (item) => item.commission?.client?.lucaAccountName || "",
    },
    {
      key: "clientCurrency",
      label: "Client Currency",
      sortable: false,
      accessor: (item) => item.commission?.client?.currency || "",
    },
    {
      key: "clientBankName",
      label: "Client Bank",
      sortable: false,
      accessor: (item) => item.commission?.client?.bankName || "",
    },
    {
      key: "clientIBAN",
      label: "Client IBAN",
      sortable: false,
      accessor: (item) => item.commission?.client?.IBAN || "",
    },
    {
      key: "clientSWIFT",
      label: "Client SWIFT",
      sortable: false,
      accessor: (item) => item.commission?.client?.SWIFT || "",
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      accessor: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString("tr-TR") : "",
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate(`/payments/${row.commission?.paymentId}/invoices/${row.id}`)}>
            View
          </Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = ["clientName", "commissionEmail", "totalAmount", "status", "country", "lucaId", "actions"];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
    >
      <input
        type="file"
        id="uploadExcel"
        style={{ display: "none" }}
        onChange={uploadExcel}
        accept=".xlsx,.xls"
      />
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        resetDefault={() => {
          setSearchParams({
            name: "",
            email: "",
            lucaId: "",
            status: "",
          });
          setQueryParams({
            name: "",
            email: "",
            lucaId: "",
            status: "",
          });
          setPage(1);
        }}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={advancedColumns}
          columnVisibilityKey="invoices-all-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)}
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={simpleColumns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)}
          renderGridItem={(item) => (
            <InvoiceCard
              key={item.id}
              name={item.commission?.client?.name}
              email={item.email}
              invoiceId={item.commission?.invoiceId}
              finalAmount={item.commission?.finalAmount}
              status={item.status}
              onDetailClick={() =>
                navigate(
                  `/payments/${item.commission.paymentId}/invoices/${item.id}`
                )
              }
            />
          )}
        />
      )}

      <PaymentDateModal
        isOpen={isPaymentDateModalOpen}
        onClose={() => {
          setIsPaymentDateModalOpen(false);
          setSelectedInvoice(null);
        }}
        onConfirm={handlePaymentDateConfirm}
        invoiceId={selectedInvoice}
      />
    </AppLayout>
  );
}
